import type { ProtocolStep } from '../schemas/protocol.js';
import type { PlayedProtocol, SessionDetail } from '../schemas/session-read.js';
import { type SessionTurn, type SnapshotObjective, turnCallTokens } from '../schemas/turn.js';

/**
 * What the Sessions screens COUNT, in one pure module (§12.1 to §12.3) - and,
 * since issue #94, what the MCP assistant counts too.
 *
 * ## Why the arithmetic is here and not in a component or a route
 *
 * Every figure below is read in at least three places - the parcours prints
 * the cost of a session, the conversation prints the cost of the protocol, and
 * `get_session` of the assistant prints the eight figures of §12.2 at the head
 * of its answer - and none of them may disagree about a euro. It used to live
 * in the frontend (`lib/session-figures.ts`); the assistant needed the same
 * numbers on the server, and a second spelling of « what this session cost »
 * is a second answer. So it moved to the one package both sides already read.
 *
 * It is also the only part of the Sessions lot that can be tested without a
 * DOM, which is the argument `lib/protocol-lanes.ts` already makes for the
 * geometry of the frise: jsdom has no layout, and a component test that
 * asserted a total would be asserting a render.
 *
 * ## Nothing here recomputes what the engine decided
 *
 * A snapshot is authoritative (§8.7): « la page Sessions rejoue l'état à
 * n'importe quel tour sans le recalculer ». So the statuses of objectives are
 * READ off the last snapshot that met them, never re-derived from the extracts,
 * and the step a turn belongs to is the `stepId` the turn recorded, never a
 * position guessed from its index. What this module does is add up and group.
 */

/* ------------------------------------------------------------- the totals */

/**
 * The turns that COUNT as turns: every one but the reread on entry (issue
 * #165, H100). The reread is a turn without an exchange - nobody spoke on it -
 * so it counts in neither the turn figures nor the malformed ones; what it
 * cost still counts, which is why the sums of euros and tokens below read
 * every row and only the counts read this.
 */
export function exchangeTurns(turns: readonly SessionTurn[]): readonly SessionTurn[] {
  return turns.filter((turn) => !turn.reread);
}

/**
 * The eight tiles of §12.2, and the seven the conversation keeps of them.
 *
 * The SESSION and its PROTOCOL part company on three figures, each for a reason
 * of its own: the duration (the session is its pages, the conversation its
 * turns), and since #149 the cost and the tokens - the determination of the
 * noyau is paid by the session and played by no turn (noyau.md §4.7, §9.1).
 * The eight tiles of the parcours read the session's; the protocol block and
 * the head of the conversation read the protocol's, whose turns and step bands
 * they sum to.
 */
export interface SessionFigures {
  readonly turnCount: number;
  readonly pageCount: number;
  readonly durationMs: number;
  /** The conversation's own span: first turn to last, null under two turns. */
  readonly protocolDurationMs: number | null;
  /**
   * The session's cost: its turns, plus the analyst of the noyau, the ranger and
   * the bubbles of the pages played in the conversation when it has them.
   */
  readonly costEur: number;
  /** The session's tokens: its turns', plus the analyst's, the ranger's and the bubbles'. */
  readonly tokens: number;
  /** What the TURNS cost - the conversation's own figure, the analyst left out. */
  readonly protocolCostEur: number;
  /** The turns' tokens alone. */
  readonly protocolTokens: number;
  readonly objectivesSucceeded: number;
  readonly objectivesTotal: number;
  /** Steps the session actually entered, and how many the protocol carries. */
  readonly stepsEntered: number;
  readonly stepsTotal: number;
  readonly incidents: number;
  readonly malformed: number;
}

/**
 * Every token of a turn, across its seats.
 *
 * Zero on a turn written before the calls existed (#67): the breakdown is
 * empty there, and an empty breakdown is not a turn that cost nothing - it is
 * a turn nobody recorded the breakdown of. The screens say so by printing the
 * cost, which IS stored, beside a token count that may be missing.
 */
export function turnTokens(turn: SessionTurn): number {
  return turn.calls.reduce((total, call) => total + turnCallTokens(call), 0);
}

/** Euros, rounded to the cent, so a sum of sums does not drift. */
export function euros(total: number): number {
  return Math.round(total * 100) / 100;
}

/**
 * The objectives of a whole protocol, in the order the steps carry them.
 *
 * Flattened rather than counted per step, because « 6 objectifs sur 6 » is a
 * figure about the SESSION: an objective of step 2 that a jump never reached
 * counts in the denominator all the same, and hiding it would make a protocol
 * look fully met because half of it was skipped.
 */
function allObjectives(protocol: PlayedProtocol | null): readonly { id: string }[] {
  if (protocol === null) return [];
  return protocol.content.steps.flatMap((step) => step.objectives.map(({ id }) => ({ id })));
}

/**
 * The statuses as they stood at the END of the session: the snapshot of the
 * last turn, which is the one the engine left behind.
 *
 * An objective the engine never met has no entry, and that is `vide` (§8.7) -
 * counted in the total, never in the successes.
 */
function finalObjectives(turns: readonly SessionTurn[]): readonly SnapshotObjective[] {
  const last = turns[turns.length - 1];
  return last === undefined ? [] : last.snapshot.objectives;
}

/** The cost and the tokens of the session's rangement - zero when it has none. */
function rangerUsage(detail: SessionDetail): { readonly costEur: number; readonly tokens: number } {
  const rangement = detail.rangement;
  if (rangement === null) return { costEur: 0, tokens: 0 };
  return {
    costEur: rangement.costEur,
    tokens: rangement.calls.reduce((total, call) => total + turnCallTokens(call), 0),
  };
}

/**
 * The cost and the tokens of the bubbles of the pages played in the
 * conversation - zero when the session has none, which is why a session without
 * a bubble does not move by a cent.
 */
function bubblesUsage(detail: SessionDetail): {
  readonly costEur: number;
  readonly tokens: number;
} {
  const bubbles = detail.conversationBubbles ?? [];
  return {
    costEur: bubbles.reduce((total, bubble) => total + bubble.costEur, 0),
    tokens: bubbles.reduce((total, bubble) => total + turnCallTokens(bubble.call), 0),
  };
}

export function sessionFigures(detail: SessionDetail): SessionFigures {
  const { turns, pages, cascade } = detail;
  const objectives = allObjectives(cascade.protocol);
  const settled = finalObjectives(turns);
  const succeeded = new Set(
    settled.filter((entry) => entry.status === 'succeeded').map((entry) => entry.objectiveId),
  );
  const first = turns[0];
  const last = turns[turns.length - 1];
  const turnsCost = turns.reduce((total, turn) => total + turn.costEur, 0);
  const turnsTokens = turns.reduce((total, turn) => total + turnTokens(turn), 0);
  // What the analyst spent (#149): the same column the list adds, so the tile
  // and the line of §12.1 print one euro.
  const analyst = detail.noyauUsage ?? { costEur: 0, tokens: 0 };
  // What the ranger spent (#177, scenario-succes.md §9): the rangement's own
  // column and calls, so the tile and the line of §12.1 print one euro. It is
  // not folded into `noyauUsage`, which stays the analyst's alone.
  const ranger = rangerUsage(detail);
  // What the bubbles of the pages played in the conversation cost (prolongation.md
  // §4.7, issue #186): one call of the interviewer's seat per bubble, added to the
  // session's cost and tokens and to nothing of the protocol's.
  const bubbles = bubblesUsage(detail);
  const exchanges = exchangeTurns(turns);

  return {
    turnCount: exchanges.length,
    pageCount: pages.length,
    durationMs: detail.session.durationMs,
    protocolDurationMs:
      first === undefined || last === undefined || first === last
        ? null
        : Math.max(0, Date.parse(last.createdAt) - Date.parse(first.createdAt)),
    costEur: euros(turnsCost + analyst.costEur + ranger.costEur + bubbles.costEur),
    tokens: turnsTokens + analyst.tokens + ranger.tokens + bubbles.tokens,
    protocolCostEur: euros(turnsCost),
    protocolTokens: turnsTokens,
    // Counted on the PROTOCOL's objectives and not on the snapshot's entries:
    // an invalidation of §8.4 can leave an entry for an objective the content
    // no longer carries, and that entry must not inflate either side.
    objectivesSucceeded: objectives.filter((objective) => succeeded.has(objective.id)).length,
    objectivesTotal: objectives.length,
    stepsEntered: new Set(turns.map((turn) => turn.stepId)).size,
    stepsTotal: cascade.protocol?.content.steps.length ?? 0,
    incidents: turns.reduce((total, turn) => total + turn.incidents.length, 0),
    malformed: exchanges.filter((turn) => turn.malformed).length,
  };
}

/* -------------------------------------------------------------- the chain */

/** One step of the chain the parcours draws under the protocol block (§12.2). */
export interface StepRun {
  readonly stepId: string;
  /** 1-based rank in the frozen protocol - the number the screens print. */
  readonly index: number;
  readonly name: string;
  /** Whether the session ever entered it; an unreached step is drawn faint. */
  readonly entered: boolean;
  readonly turnCount: number;
  /** The highest attempt seen on it (§8.3); 1 unless the step was replayed. */
  readonly attempts: number;
  readonly costEur: number;
  readonly objectivesSucceeded: number;
  readonly objectivesTotal: number;
  /** True when every objective it carries ended `succeeded`. */
  readonly complete: boolean;
}

/**
 * The chain of §12.2: every step of the frozen protocol, annotated with what
 * this session did in it.
 *
 * In PROTOCOL order and not in the order the session walked them, and that is
 * the decision worth naming. A session that jumps back to step 2 walked
 * `1, 2, 3, 2`, and drawing that as the chain would make one protocol look like
 * four steps. The chain is the protocol; what the session did to it is in the
 * annotations, where a replay shows as « 2 essais ».
 */
export function stepRuns(detail: SessionDetail): readonly StepRun[] {
  const protocol = detail.cascade.protocol;
  if (protocol === null) return [];
  const settled = finalObjectives(detail.turns);
  const succeeded = new Set(
    settled.filter((entry) => entry.status === 'succeeded').map((entry) => entry.objectiveId),
  );

  return protocol.content.steps.map((step: ProtocolStep, rank: number) => {
    const played = detail.turns.filter((turn) => turn.stepId === step.id);
    const reached = step.objectives.filter((objective) => succeeded.has(objective.id)).length;
    return {
      stepId: step.id,
      index: rank + 1,
      name: step.name,
      // A step closed by its own reread (#165) was entered all the same.
      entered: played.length > 0,
      turnCount: exchangeTurns(played).length,
      attempts: played.reduce((highest, turn) => Math.max(highest, turn.snapshot.attempt), 1),
      costEur: euros(played.reduce((total, turn) => total + turn.costEur, 0)),
      objectivesSucceeded: reached,
      objectivesTotal: step.objectives.length,
      complete: played.length > 0 && reached === step.objectives.length,
    };
  });
}

/* -------------------------------------------------------- the step bands */

/**
 * Where a step BAND opens in the transcript of §12.3.
 *
 * A band is drawn on the turn that changed step, and also on a turn that
 * replayed the same step: « relancer l'étape » starts a new pass with its
 * counters at zero (§8.4), and a reader who saw no band would read the second
 * attempt as a continuation of the first. So the key is the step AND the
 * attempt, which is exactly what the snapshot already distinguishes.
 */
export interface StepBand {
  readonly stepId: string;
  readonly index: number;
  readonly name: string;
  readonly attempt: number;
  /** The cap of §7.2, null on a step whose plafond was never posed. */
  readonly maxTurns: number | null;
  readonly attemptsAllowed: number;
  /** Turns played on THIS attempt, and what they cost. */
  readonly turnCount: number;
  readonly costEur: number;
}

/**
 * The band to draw above a turn, or null when the turn continues what the
 * previous one was already doing.
 *
 * It takes the whole list rather than two turns because the figures it prints
 * are about the PASS and not about the turn: « 4 tours sur 6 · essai 2 » counts
 * every turn of that pass, including those still to come further down the fil,
 * which is what makes the band readable before scrolling to the end of it.
 */
export function stepBandAt(detail: SessionDetail, position: number): StepBand | null {
  const turn = detail.turns[position];
  if (turn === undefined) return null;
  const previous = detail.turns[position - 1];
  const sameRun =
    previous !== undefined &&
    previous.stepId === turn.stepId &&
    previous.snapshot.attempt === turn.snapshot.attempt;
  if (sameRun) return null;

  const steps = detail.cascade.protocol?.content.steps ?? [];
  const rank = steps.findIndex((step) => step.id === turn.stepId);
  const step = rank < 0 ? undefined : steps[rank];
  const run = detail.turns.filter(
    (other) => other.stepId === turn.stepId && other.snapshot.attempt === turn.snapshot.attempt,
  );

  return {
    stepId: turn.stepId,
    // A step the frozen protocol no longer carries keeps its turns readable:
    // rank 0 rather than a crash, and the name falls back to the identifier.
    index: rank + 1,
    name: step?.name ?? turn.stepId,
    attempt: turn.snapshot.attempt,
    maxTurns: step?.rules.maxTurns ?? null,
    attemptsAllowed: step?.rules.attempts ?? 1,
    // The reread row opens the band of its step and costs in it, but it is
    // no turn of the pass (#165, H100).
    turnCount: exchangeTurns(run).length,
    costEur: euros(run.reduce((total, other) => total + other.costEur, 0)),
  };
}
